import React, { useState, useEffect, useRef } from 'react';
import { Clock, Trash2, Volume2, Mic, ArrowRightLeft, ChevronLeft, ChevronRight, Send, Search, Film, ExternalLink } from 'lucide-react';
import { getHistory, deleteGeneration } from '../../services/speechApi';
import { toast, confirm as confirmModal } from '../ui/Toast';
import SpeechAudioPlayer from './SpeechAudioPlayer';
import ExportToProject from './ExportToProject';

const TYPE_CONFIG = {
  tts: { label: 'Synthèse', icon: Volume2, color: '#3b82f6' },
  stt: { label: 'Transcription', icon: Mic, color: '#f59e0b' },
  sts: { label: 'Conversion', icon: ArrowRightLeft, color: '#8b5cf6' },
  reel: { label: 'Reel / TikTok', icon: Film, color: '#f97316' },
};

const isReelGeneration = (gen) =>
  gen?.provider === 'local-h2'
  || gen?.options?.source === 'reel'
  || (typeof gen?.options?.source_url === 'string' && /^https?:\/\//i.test(gen.options.source_url));

const reelSourceUrl = (gen) =>
  gen?.options?.source_url
  || (typeof gen?.input_filename === 'string' && /^https?:\/\//i.test(gen.input_filename) ? gen.input_filename : null);

const ITEMS_PER_PAGE = 10;

const SpeechHistory = () => {
  const [generations, setGenerations] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState(null); // null = tous
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [exportGen, setExportGen] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    loadHistory();
  }, [filter, page, searchQuery]);

  // Debounce search (400ms)
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchQuery(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const data = await getHistory(filter, ITEMS_PER_PAGE, (page - 1) * ITEMS_PER_PAGE, searchQuery || null);
      setGenerations(data.generations || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Erreur chargement historique');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmModal('Supprimer cette génération ?')) return;
    try {
      await deleteGeneration(id);
      setGenerations(prev => prev.filter(g => g.id !== id));
      setTotal(prev => prev - 1);
      toast.success('Supprimé');
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

  return (
    <div style={{ padding: '24px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Filtres */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexShrink: 0 }}>
        <Clock size={16} style={{ color: 'var(--text-secondary)' }} />
        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>Historique</span>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>({total})</span>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={13} style={{ position: 'absolute', left: 8, color: 'var(--text-muted)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..."
              style={{
                padding: '5px 10px 5px 28px', borderRadius: 8, border: '1px solid var(--card-border)',
                background: 'var(--card-bg)', color: 'var(--text)', fontSize: 11, width: 160,
                outline: 'none', transition: 'border-color 0.15s',
              }}
              onFocus={e => e.target.style.borderColor = '#3b82f6'}
              onBlur={e => e.target.style.borderColor = 'var(--card-border)'}
            />
          </div>
          <div style={{ display: 'flex', gap: '4px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '8px', padding: '2px' }}>
            {[
              { id: null, label: 'Tous' },
              { id: 'tts', label: 'TTS' },
              { id: 'stt', label: 'STT' },
              { id: 'sts', label: 'STS' },
              { id: 'reel', label: 'Reel' },
            ].map(f => (
              <button key={f.id || 'all'} onClick={() => { setFilter(f.id); setPage(1); }}
                style={{
                  padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                  background: filter === f.id ? 'rgba(29,78,216,0.15)' : 'transparent',
                  color: filter === f.id ? '#3b82f6' : 'var(--text-secondary)',
                  fontSize: '11px', fontWeight: filter === f.id ? 600 : 500, transition: 'all 0.15s',
                }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Liste */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Chargement...</div>
        ) : generations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', opacity: 0.5 }}>
            <Clock size={36} style={{ color: 'var(--text-secondary)', marginBottom: '12px' }} />
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Aucune génération</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {generations.map(gen => {
              const reel = isReelGeneration(gen);
              const cfg = reel ? TYPE_CONFIG.reel : (TYPE_CONFIG[gen.generation_type] || TYPE_CONFIG.tts);
              const Icon = cfg.icon;
              const isExpanded = expandedId === gen.id;
              const sourceUrl = reelSourceUrl(gen);

              return (
                <div key={gen.id} style={{
                  background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                  borderRadius: '12px', borderLeft: `3px solid ${cfg.color}`,
                  transition: 'all 0.15s',
                }}>
                  <div onClick={() => setExpandedId(isExpanded ? null : gen.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                      cursor: 'pointer',
                    }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
                      background: `${cfg.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon size={14} style={{ color: cfg.color }} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {gen.generation_type === 'stt'
                          ? (gen.output_text?.substring(0, 80) || gen.input_filename || 'Transcription')
                          : (gen.input_text?.substring(0, 80) || gen.input_filename || 'Audio')}
                        {((gen.input_text || gen.output_text || '')?.length > 80) && '…'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', gap: '8px', marginTop: '2px', flexWrap: 'wrap' }}>
                        <span>{cfg.label}</span>
                        <span>·</span>
                        <span>{gen.provider === 'local-h2' ? 'H2 souverain' : gen.provider}</span>
                        <span>·</span>
                        <span>{gen.model}</span>
                        {gen.voice_id && <><span>·</span><span>{gen.voice_name || gen.voice_id}</span></>}
                        {sourceUrl && (
                          <>
                            <span>·</span>
                            <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {(() => { try { return new URL(sourceUrl).hostname.replace(/^www\./, ''); } catch { return 'URL'; } })()}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{formatDate(gen.created_at)}</div>
                      {gen.estimated_cost_usd > 0 && (
                        <div style={{ fontSize: '10px', color: cfg.color, fontWeight: 600 }}>
                          ${parseFloat(gen.estimated_cost_usd).toFixed(4)}
                        </div>
                      )}
                      {reel && Number(gen.estimated_cost_usd) === 0 && (
                        <div style={{ fontSize: '10px', color: cfg.color, fontWeight: 600 }}>souverain</div>
                      )}
                    </div>

                    <button onClick={(e) => { e.stopPropagation(); setExportGen(gen); }}
                      style={{
                        background: 'none', border: 'none', color: '#3b82f6',
                        cursor: 'pointer', padding: '4px', borderRadius: '4px', opacity: 0.5,
                        transition: 'opacity 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
                      title="Envoyer vers un projet">
                      <Send size={14} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(gen.id); }}
                      style={{
                        background: 'none', border: 'none', color: 'var(--text-secondary)',
                        cursor: 'pointer', padding: '4px', borderRadius: '4px', opacity: 0.5,
                        transition: 'opacity 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
                      title="Supprimer">
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--card-border)' }}>
                      {gen.output_url && (gen.generation_type === 'tts' || gen.generation_type === 'sts') && (
                        <div style={{ marginTop: '12px' }}>
                          <SpeechAudioPlayer blobUrl={gen.output_url} filename={gen.output_filename || 'audio.mp3'} />
                        </div>
                      )}

                      {gen.generation_type === 'stt' && gen.output_text && (
                        <div style={{
                          marginTop: '12px', padding: '12px', borderRadius: '8px',
                          background: 'var(--input-bg)', fontSize: '13px', color: 'var(--text)',
                          lineHeight: 1.6, maxHeight: '200px', overflow: 'auto', whiteSpace: 'pre-wrap',
                        }}>
                          {gen.output_text}
                        </div>
                      )}

                      {gen.generation_type === 'tts' && gen.input_text && (
                        <div style={{
                          marginTop: '8px', padding: '10px', borderRadius: '8px',
                          background: 'var(--input-bg)', fontSize: '12px', color: 'var(--text-secondary)',
                          lineHeight: 1.5, maxHeight: '120px', overflow: 'auto', whiteSpace: 'pre-wrap',
                        }}>
                          <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Script :</span>
                          {gen.input_text}
                        </div>
                      )}

                      {sourceUrl && (
                        <a
                          href={sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
                            fontSize: 12, color: '#f97316', textDecoration: 'none', fontWeight: 500,
                            maxWidth: '100%', overflow: 'hidden',
                          }}
                        >
                          <ExternalLink size={12} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sourceUrl}</span>
                        </a>
                      )}

                      <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {gen.characters > 0 && <span>{gen.characters} chars</span>}
                        {gen.input_size_bytes > 0 && <span>{(gen.input_size_bytes / 1024).toFixed(0)} Ko</span>}
                        {gen.duration_seconds > 0 && <span>{Math.round(gen.duration_seconds)} s</span>}
                        {gen.language && <span>Langue: {gen.language}</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {exportGen && (
        <ExportToProject
          generation={exportGen}
          onClose={() => setExportGen(null)}
          onExported={() => setExportGen(null)}
        />
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', paddingTop: '16px', flexShrink: 0 }}>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            style={{ ...navBtnStyle, opacity: page <= 1 ? 0.3 : 1 }}>
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            style={{ ...navBtnStyle, opacity: page >= totalPages ? 0.3 : 1 }}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

const navBtnStyle = {
  padding: '6px', borderRadius: '6px', border: '1px solid var(--card-border)',
  background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
  display: 'flex', alignItems: 'center',
};

export default SpeechHistory;
